import {advantageMarker, createTariff, MAX_TARIFFS, MOVABLE_FIELDS} from './model'
import type {AdvantageMarker, MovableFieldKey, Tariff} from './model'
import {createOrderForm, ORDER_FORM_FIELDS} from './orderForm'
import type {OrderForm, OrderFormFieldKey} from './orderForm'
import {normalizePrice, PRICE_FORMATS} from './price'
import type {PriceFormat} from './price'

export const PRICE_UNITS = ['kzt', 'kzt_month', 'kzt_day', 'kzt_week', 'kzt_year'] as const
type PriceUnit = typeof PRICE_UNITS[number]
type ApiTariffFieldKey = 'title' | MovableFieldKey
type ApiFormFieldType = 'name' | 'phone' | 'mail' | 'address' | 'comment'
type ApiOrderForm = {
  title: string; subtitle: string; submitButtonText: string; afterSubmitTitle: string; afterSubmitText: string
  fields: {type: ApiFormFieldType; enabled: boolean; required: boolean}[]
}
export type ApiTariff = {
  id: number
  order: number
  enabled: {photo: boolean; title: boolean; description: boolean; advantages: boolean; price: boolean; button: boolean; additionalText: boolean}
  photo: number | null
  title: string
  description: string
  advantages: {list: {text: string}[]}
  price: {value: number | null; unit: PriceUnit | null; oldPrice: number | null; showOldPrice: boolean}
  button: {text: string; color: string; radius: 'none' | 'default' | 'big'; form: ApiOrderForm}
  additionalText: string
  fieldOrder: ApiTariffFieldKey[]
}
export type TariffsData = {
  color: string
  radius: string
  photoPosition: 'top' | 'titleBackground'
  photoOverlayOpacity: number
  lightText: boolean
  titleAlignment: 'center' | 'left'
  layout: 'carousel' | 'list'
  equalCardHeight: boolean
  advantagesIcon: AdvantageMarker
  tariffs: ApiTariff[]
  sharedOrderForm: boolean
}

const record = (value: unknown): Record<string, any> => value && typeof value === 'object' && !Array.isArray(value) ? value : {}
const text = (value: unknown) => typeof value === 'string' ? value : ''
const amountText = (value: unknown) => typeof value === 'number' ? String(value) : text(value)
const formKey = (type: unknown): OrderFormFieldKey | undefined => {
  const key = type === 'mail' ? 'email' : type
  return ORDER_FORM_FIELDS.includes(key as OrderFormFieldKey) ? key as OrderFormFieldKey : undefined
}
const formFromApi = (value: unknown): OrderForm => {
  const source = record(value)
  const fields = Array.isArray(source.fields) ? source.fields : []
  return createOrderForm({
    title: source.title, subtitle: source.subtitle, submitText: source.submitButtonText,
    successTitle: source.afterSubmitTitle, successMessage: source.afterSubmitText,
    fields: Object.fromEntries(fields.flatMap(field => {
      const key = formKey(field?.type)
      return key ? [[key, {enabled: field.enabled === true, required: field.enabled === true && field.required === true}]] : []
    })) as OrderForm['fields'],
    order: fields.map(field => formKey(field?.type)).filter((key): key is OrderFormFieldKey => !!key)
  })
}

// Existing drafts used the editor's structure. Continue reading them while writing the API contract.
export const tariffFromApi = (value: unknown): Tariff => {
  const source = record(value)
  if (source.fields) return createTariff(source as Tariff)
  const enabled = record(source.enabled)
  const price = record(source.price)
  const button = record(source.button)
  const unitIndex = PRICE_UNITS.indexOf(price.unit)
  const sourceFields = {
    image: {enabled: enabled.photo === true, value: '',
      image: Number.isSafeInteger(source.photo) && source.photo > 0 ? {imageId: source.photo, mimeType: 'image/jpeg'} : undefined},
    name: {enabled: enabled.title === true, value: text(source.title)},
    description: {enabled: enabled.description === true, value: text(source.description)},
    advantages: {enabled: enabled.advantages === true, value: '', items: record(source.advantages).list},
    price: {enabled: enabled.price === true, value: amountText(price.value), format: PRICE_FORMATS[Math.max(0, unitIndex)],
      oldPrice: {enabled: price.showOldPrice === true, value: amountText(price.oldPrice)}},
    button: {enabled: enabled.button === true, value: text(button.text),
      buttonStyle: {color: button.color, radius: button.radius === 'none' ? 'no' : button.radius}},
    additionalText: {enabled: enabled.additionalText === true, value: text(source.additionalText)}
  }
  return createTariff({id: source.id, photo: source.photo, fields: sourceFields,
    order: Array.isArray(source.fieldOrder) ? source.fieldOrder : [...MOVABLE_FIELDS],
    orderForm: formFromApi(button.form)} as Tariff)
}

export const tariffsFromApi = (settings: unknown): Tariff[] => {
  const tariffs = record(settings).tariffs
  if (!Array.isArray(tariffs) || !tariffs.length) return [createTariff()]
  return tariffs.map((tariff, index) => ({tariff, index}))
    .sort((a, b) => (typeof a.tariff?.order === 'number' ? a.tariff.order : a.index + 1)
      - (typeof b.tariff?.order === 'number' ? b.tariff.order : b.index + 1))
    .slice(0, MAX_TARIFFS).map(({tariff}) => tariffFromApi(tariff))
}

const amount = (value: string): number | null => {
  const normalized = normalizePrice(value)
  if (normalized === null) throw new Error('Invalid tariff price')
  return normalized ? Number(normalized) : null
}
const formToApi = (form: OrderForm): ApiOrderForm => ({
  title: form.title, subtitle: form.subtitle, submitButtonText: form.submitText,
  afterSubmitTitle: form.successTitle, afterSubmitText: form.successMessage,
  fields: form.order.map(key => ({type: key === 'email' ? 'mail' : key,
    enabled: form.fields[key].enabled, required: form.fields[key].enabled && form.fields[key].required}))
})

export const tariffToApi = (tariff: Tariff, index: number): ApiTariff => {
  const fields = tariff.fields
  const value = amount(fields.price.value)
  return {
    id: tariff.id, order: index + 1, fieldOrder: ['title', ...tariff.order],
    enabled: {photo: fields.image.enabled, title: fields.name.enabled, description: fields.description.enabled,
      advantages: fields.advantages.enabled, price: fields.price.enabled, button: fields.button.enabled,
      additionalText: fields.additionalText.enabled},
    photo: fields.image.image?.imageId ?? tariff.photo ?? null,
    title: fields.name.value, description: fields.description.value,
    advantages: {list: (fields.advantages.items || []).filter(item => item.text.trim()).map(item => ({text: item.text}))},
    price: {value, unit: value === null ? null : PRICE_UNITS[PRICE_FORMATS.indexOf(fields.price.format as PriceFormat)] || 'kzt',
      oldPrice: amount(fields.price.oldPrice?.value || ''), showOldPrice: fields.price.oldPrice?.enabled === true},
    button: {text: fields.button.value, color: fields.button.buttonStyle?.color || '#0D87EF',
      radius: fields.button.buttonStyle?.radius === 'no' ? 'none' : fields.button.buttonStyle?.radius || 'default',
      form: formToApi(tariff.orderForm)},
    additionalText: fields.additionalText.value
  }
}

export const tariffsDataToApi = (tariffs: Tariff[], marker: AdvantageMarker, settings: unknown): TariffsData => {
  if (!tariffs.length) throw new Error('At least one tariff is required')
  const source = record(settings)
  const {tariffs: ignoredTariffs, advantagesMarker: ignoredMarker, sharedOrderForm, ...retained} = source
  return {
    color: '#FFFFFF', radius: 'default', photoPosition: 'top', photoOverlayOpacity: 45, lightText: true,
    titleAlignment: 'center', layout: 'carousel', equalCardHeight: false,
    ...retained, sharedOrderForm: tariffs.length > 1 && sharedOrderForm === true,
    advantagesIcon: advantageMarker(marker), tariffs: tariffs.map(tariffToApi)
  }
}
